import i18n from '@i18next/i18n';
import CommonTips, { CommonTipsActionType, CommonTipsType } from '@com/common-tips';
import { ILogger, SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { EditEvents } from '@src/slide/domain/slide-app/core/interfaces/event.interface';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import set from 'lodash/set';
import { SlideAppError } from '@tencent/slide-sdk-revision';
import { ErrCodeListener } from './err-code-listener';
import { IServerMessage, IServerMessageHead } from '@tencent/docs-connection';
import { ISocketListener } from './socket-listener';

export default class RevisionListener extends ErrCodeListener {
    private logger: ILogger;
    public constructor(private socketListener: ISocketListener) {
        super();
        SlideDataServices.Infra.Logger.promise().then((logger) => {
            this.logger = logger;
        });

        SlideDataServices.SDKDataServices.EventManager.promise().then(({ revisionEvents, socketEvents }) => {
            revisionEvents.onCommitSucceed(this.revisionCommitSuccessCb);
            revisionEvents.onCommitFail(this.revisionCommitFailCb);
            revisionEvents.onEnqueue(() => SlideDataServices.DocInfoManager()?.setDocVisible());
            revisionEvents.onFetchMissingDataError(this.fetchMissingDataErrorCb);
            revisionEvents.onUpdateBaseRevision((rev) => {
                const clientVars = SlideDataServices.ClientVarsService()?.getClientVars();
                if (clientVars) {
                    set(clientVars, 'collab_client_vars.rev', rev);
                }
            });
            socketEvents.onRevertMessage(this.revertDocCb);
        });
    }

    public onSetVisibleFailed(cb: () => void) {
        SlideDataServices.EventManager.promise().then((editEvents) => {
            editEvents.on(EditEvents.SetVisibleFailed, cb);
        });
    }

    private revisionCommitSuccessCb = () => {
        this.socketListener.closeOfflineTips();
    };

    private revisionCommitFailCb = (info: { code: number, error: SlideAppError }) => {
        this.handleSlideAppErrorCode(info);
    };

    private revertDocCb = (data?: IServerMessage) => {
        const { uid } = (data?.head || {}) as IServerMessageHead;
        const tinyid = SlideDataServices.DocInfoManager()?.tinyid;
        // 如果操作用户是自己, 则屏蔽弹窗
        if (uid === tinyid) return;

        const commonTipsConfig = {
            message: i18n.t('文档已被其他协作者还原，请刷新'),
            commonTipsType: CommonTipsType.INFO,
            onlyShowOne: true,
            actionType: CommonTipsActionType.REFRESH,
        };
        CommonTips.getInstance().show(commonTipsConfig);
    };

    private fetchMissingDataErrorCb = async (docIsReverted: boolean) => {
        CommonTips.getInstance().hide();
        if (docIsReverted) {
            const collaborationManager = await OnlineEditServices.CollaborationManager.promise();
            if (collaborationManager.getPersistent().getMutationsPacks().length) {
                CommonTips.getInstance().show({
                    message: i18n.t('文档已被其他协作者还原，本地存在未保存的数据'),
                    commonTipsType: CommonTipsType.INFO,
                    onlyShowOne: true,
                    actionType: CommonTipsActionType.EXPORT_SNAP_SHOT
                            | CommonTipsActionType.CREATE_COPY_DOC,
                    callBack: (actionType: number, fn?: any) => {
                        if (actionType === CommonTipsActionType.CREATE_COPY_DOC) {
                            fn(true);
                        }
                    },
                });
                return;
            }
            this.revertDocCb();
        } else {
            CommonTips.getInstance().show({
                message: i18n.t('拉取增量数据失败，请刷新重试'),
                commonTipsType: CommonTipsType.INFO,
                onlyShowOne: true,
                actionType: CommonTipsActionType.REFRESH,
            });
        }
    };
}
